import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../components/ui/Button';
import { Printer, MapPin, Phone, Instagram, Check } from 'lucide-react';
type PrintMode = 'front' | 'back' | 'both' | 'single' | null;
export function RaffleVoucherPage() {
  const [printMode, setPrintMode] = useState<PrintMode>(null);
  const handlePrint = useCallback((mode: PrintMode) => {
    setPrintMode(mode);
  }, []);
  useEffect(() => {
    if (printMode) {
      const timeout = setTimeout(() => {
        window.print();
        setPrintMode(null);
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [printMode]);
  const printClass = printMode ? `print-mode-${printMode}` : '';
  return <div className={`min-h-screen bg-gray-100 p-4 sm:p-8 print:p-0 print:bg-white flex flex-col items-center ${printClass}`} data-id="element-2557">
      {/* Controls - Hidden when printing */}
      <div className="max-w-[210mm] w-full mb-8 no-print" data-id="element-2558">
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between mb-6" data-id="element-2559">
          <div data-id="element-2560">
            <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2561">
              Raffle Gift Voucher
            </h1>
            <p className="text-gray-600 text-sm" data-id="element-2562">
              Print each side separately as A5, or both together on A4
              Landscape.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 items-center" data-id="element-2563">
          <Button onClick={() => handlePrint('front')} className="gap-2 shadow-lg" data-id="element-2564">
            <Printer className="w-4 h-4" data-id="element-2565" />
            Print Front Side
          </Button>
          <Button onClick={() => handlePrint('back')} className="gap-2 shadow-lg" data-id="element-2566">
            <Printer className="w-4 h-4" data-id="element-2567" />
            Print Back Side
          </Button>
          <Button onClick={() => handlePrint('both')} variant="outline" className="gap-2" data-id="element-2568">
            <Printer className="w-4 h-4" data-id="element-2569" />
            Print Both (A4 Landscape)
          </Button>
          <Button onClick={() => handlePrint('single')} variant="outline" className="gap-2" data-id="element-2570">
            <Printer className="w-4 h-4" data-id="element-2571" />
            Print All on One Page (A4)
          </Button>
        </div>
        <p className="text-xs text-gray-500 mt-3" data-id="element-2572">
          <strong data-id="element-2573">Front / Back:</strong> Set paper to{' '}
          <strong data-id="element-2574">A5 Portrait</strong>, margins <strong data-id="element-2575">None</strong>, enable{' '}
          <strong data-id="element-2576">Background graphics</strong>.<br data-id="element-2577" />
          <strong data-id="element-2578">Both:</strong> Set paper to <strong data-id="element-2579">A4 Landscape</strong>,
          margins <strong data-id="element-2580">None</strong>, enable{' '}
          <strong data-id="element-2581">Background graphics</strong>.
        </p>
      </div>

      {/* ═══════════════════════════════════════════ */}
      {/* FRONT SIDE CARD (Screen Preview + Print)   */}
      {/* ═══════════════════════════════════════════ */}
      <div className="no-print mb-4 text-sm font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2582">
        Front Side
      </div>
      <div className="front-panel bg-white shadow-2xl overflow-hidden relative mb-10 print-panel" style={{
      WebkitPrintColorAdjust: 'exact',
      printColorAdjust: 'exact'
    } as React.CSSProperties} data-id="element-2583">
        {/* Background Image */}
        <img src="https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&w=1200&q=80" alt="Dentist and patient smiling" className="absolute inset-0 w-full h-full object-cover z-0" data-id="element-2584" />

        {/* Dark overlay */}
        <div className="absolute inset-0 z-[1]" style={{
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.50), rgba(0,0,0,0.35), rgba(0,0,0,0.55))',
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact'
      } as React.CSSProperties} data-id="element-2585"></div>

        {/* Elegant Double Border Inset */}
        <div className="absolute inset-5 border z-[2] pointer-events-none" style={{
        borderColor: 'rgba(255,255,255,0.25)'
      }} data-id="element-2586"></div>
        <div className="absolute inset-7 border z-[2] pointer-events-none" style={{
        borderColor: 'rgba(255,255,255,0.10)'
      }} data-id="element-2587"></div>

        {/* Content */}
        <div className="relative z-[3] flex flex-col items-center w-full h-full justify-between py-10 px-10 text-center" data-id="element-2588">
          {/* Top Branding */}
          <div className="flex flex-col items-center" data-id="element-2589">
            <div className="rounded-xl p-3 mb-4" style={{
            backgroundColor: 'rgba(255,255,255,0.95)'
          }} data-id="element-2590">
              <img src="https://cdn.magicpatterns.com/uploads/oHJPxkkc7f6GgU5uyo3LQX/ChatGPT_Image_Feb_5,_2026,_03_12_40_PM.png" alt="Dentico Clinic Logo" className="h-12 w-auto object-contain" data-id="element-2591" />
            </div>
            <span className="text-2xl font-bold tracking-tight leading-none mb-2" style={{
            color: '#ffffff',
            textShadow: '0 1px 3px rgba(0,0,0,0.3)'
          }} data-id="element-2592">
              DenticoClinic
            </span>
            <p className="text-sm uppercase leading-tight" style={{
            color: 'rgba(255,255,255,0.85)',
            letterSpacing: '0.3em',
            textShadow: '0 1px 2px rgba(0,0,0,0.3)'
          }} data-id="element-2593">
              Private Dentistry
            </p>
          </div>

          {/* Center Content */}
          <div className="flex flex-col items-center w-full" data-id="element-2594">
            <div className="w-12 h-px mb-7" style={{
            backgroundColor: 'rgba(255,255,255,0.4)'
          }} data-id="element-2595"></div>

            <p className="text-xs uppercase font-medium mb-5" style={{
            color: 'rgba(255,255,255,0.75)',
            letterSpacing: '0.4em',
            textShadow: '0 1px 2px rgba(0,0,0,0.3)'
          }} data-id="element-2596">
              Gift Voucher
            </p>

            <h2 className="text-4xl font-serif mb-8 leading-tight" style={{
            color: '#ffffff',
            textShadow: '0 1px 4px rgba(0,0,0,0.3)'
          }} data-id="element-2597">
              Professional
              <br data-id="element-2598" />
              Teeth Whitening
            </h2>

            <div className="inline-block py-4 px-12" style={{
            borderTop: '1px solid rgba(255,255,255,0.35)',
            borderBottom: '1px solid rgba(255,255,255,0.35)'
          }} data-id="element-2599">
              <p className="text-[10px] uppercase mb-2" style={{
              color: 'rgba(255,255,255,0.65)',
              letterSpacing: '0.3em'
            }} data-id="element-2600">
                Value
              </p>
              <p className="text-3xl font-serif font-bold tracking-wide" style={{
              color: '#ffffff',
              textShadow: '0 1px 3px rgba(0,0,0,0.3)'
            }} data-id="element-2601">
                £280
              </p>
            </div>
          </div>

          {/* Bottom */}
          <div className="mt-auto" data-id="element-2602">
            <p className="text-xs" style={{
            color: 'rgba(255,255,255,0.65)',
            letterSpacing: '0.2em',
            textShadow: '0 1px 2px rgba(0,0,0,0.3)'
          }} data-id="element-2603">
              denticoclinic.co.uk
            </p>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════ */}
      {/* BACK SIDE CARD (Screen Preview + Print)    */}
      {/* ═══════════════════════════════════════════ */}
      <div className="no-print mb-4 text-sm font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2604">
        Back Side
      </div>
      <div className="back-panel overflow-hidden relative print-panel" style={{
      backgroundColor: '#fafaf9',
      WebkitPrintColorAdjust: 'exact',
      printColorAdjust: 'exact',
      boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
    } as React.CSSProperties} data-id="element-2605">
        {/* Elegant Border */}
        <div className="absolute inset-5 pointer-events-none" style={{
        border: '1px solid rgba(15,60,60,0.10)'
      }} data-id="element-2606"></div>
        <div className="absolute inset-7 pointer-events-none" style={{
        border: '1px solid rgba(15,60,60,0.05)'
      }} data-id="element-2607"></div>

        {/* Content wrapper */}
        <div className="relative z-10 flex flex-col py-8 px-10 items-center text-center h-full" data-id="element-2608">
          {/* Top Branding */}
          <div className="flex flex-col items-center mb-5 mt-1" data-id="element-2609">
            <img src="https://cdn.magicpatterns.com/uploads/oHJPxkkc7f6GgU5uyo3LQX/ChatGPT_Image_Feb_5,_2026,_03_12_40_PM.png" alt="Dentico Clinic Logo" className="h-8 w-auto object-contain mb-1.5" data-id="element-2610" />
            <span className="text-base font-bold tracking-tight leading-none mb-1" style={{
            color: '#111827'
          }} data-id="element-2611">
              Dentico
              <span style={{
              color: '#0d9488'
            }} data-id="element-2612">
                Clinic
              </span>
            </span>
            <p className="text-[9px] uppercase leading-tight" style={{
            color: '#6b7280',
            letterSpacing: '0.2em'
          }} data-id="element-2613">
              Private Dentistry
            </p>
          </div>

          {/* Title */}
          <h1 className="text-xl font-serif mb-4 tracking-wide" style={{
          color: '#134e4a'
        }} data-id="element-2614">
            Gift Voucher
          </h1>

          <div className="max-w-[88%] space-y-3 mb-5" data-id="element-2615">
            <p className="text-[13px] leading-relaxed" style={{
            color: '#4b5563'
          }} data-id="element-2616">
              Congratulations! You have received a complimentary professional
              teeth whitening experience at Dentico Dental Clinic.
            </p>
            <p className="text-[13px] leading-relaxed" style={{
            color: '#4b5563'
          }} data-id="element-2617">
              We look forward to welcoming you to our clinic and helping you
              achieve a brighter, confident smile.
            </p>
          </div>

          {/* What's Included */}
          <div className="mb-5 w-full max-w-[85%]" data-id="element-2618">
            <h3 className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{
            color: '#115e59'
          }} data-id="element-2619">
              What's Included
            </h3>
            <ul className="space-y-2 text-left inline-block" data-id="element-2620">
              {['Professional clinical teeth whitening', 'Consultation with our dentist', 'Personalised treatment approach'].map((item, idx) => <li key={idx} className="flex items-start gap-2.5" data-id="element-2621">
                  <div className="mt-0.5 w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0" style={{
                backgroundColor: '#ccfbf1',
                WebkitPrintColorAdjust: 'exact',
                printColorAdjust: 'exact'
              } as React.CSSProperties} data-id="element-2622">
                    <Check className="w-2 h-2" style={{
                  color: '#0f766e'
                }} data-id="element-2623" />
                  </div>
                  <span className="text-[13px]" style={{
                color: '#374151'
              }} data-id="element-2624">
                    {item}
                  </span>
                </li>)}
            </ul>
          </div>

          {/* Unique Code & Validity */}
          <div className="w-full max-w-[180px] mb-5" data-id="element-2625">
            <div className="mb-3" data-id="element-2626">
              <label className="block text-[9px] uppercase tracking-widest mb-1" style={{
              color: '#6b7280'
            }} data-id="element-2627">
                Unique Code
              </label>
              <div contentEditable suppressContentEditableWarning className="w-full text-sm font-mono text-center pb-1 min-h-[1.5em] focus:outline-none" style={{
              color: '#374151',
              borderBottom: '1px solid #d1d5db'
            }} data-id="element-2628">
                DC-RAFFLE-01
              </div>
            </div>
            <p className="text-[10px] text-center tracking-wider" style={{
            color: '#6b7280'
          }} data-id="element-2629">
              Valid until: 30 June 2026
            </p>
          </div>

          {/* Footer Section */}
          <div className="mt-auto w-full flex flex-col items-center" data-id="element-2630">
            <p className="text-[9px] uppercase tracking-wider mb-3" style={{
            color: '#9ca3af'
          }} data-id="element-2631">
              By appointment only • Non-transferable
            </p>

            {/* Address + Contact Combined */}
            <div className="flex flex-col items-center text-[10px] mb-4 leading-relaxed" style={{
            color: '#4b5563'
          }} data-id="element-2632">
              <div className="flex items-center gap-1.5 mb-0.5" data-id="element-2633">
                <MapPin className="w-2.5 h-2.5" style={{
                color: '#0d9488'
              }} data-id="element-2634" />
                <span className="font-medium" style={{
                color: '#374151'
              }} data-id="element-2635">
                  Central London – Angel
                </span>
              </div>
              <span data-id="element-2636">360–364 City Road, London EC1V 2PY</span>
              <div className="flex items-center justify-center gap-4 mt-1.5" data-id="element-2637">
                <div className="flex items-center gap-1" data-id="element-2638">
                  <Phone className="w-2.5 h-2.5" style={{
                  color: '#0d9488'
                }} data-id="element-2639" />
                  <span data-id="element-2640">+44 7345 197820</span>
                </div>
                <div className="flex items-center gap-1" data-id="element-2641">
                  <Instagram className="w-2.5 h-2.5" style={{
                  color: '#0d9488'
                }} data-id="element-2642" />
                  <span data-id="element-2643">@dentico_clinic</span>
                </div>
              </div>
            </div>

            {/* Two QR Codes Row */}
            <div className="flex items-start justify-center gap-8 w-full" data-id="element-2644">
              {/* QR 1: Instagram */}
              <div className="flex flex-col items-center" data-id="element-2645">
                <div className="w-16 h-16 rounded-md overflow-hidden p-1" style={{
                border: '1px solid #99f6e4',
                backgroundColor: '#ffffff'
              }} data-id="element-2646">
                  <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://instagram.com/dentico_clinic&format=png&color=0d4f4f&bgcolor=ffffff" alt="Instagram QR Code" className="w-full h-full object-contain" data-id="element-2647" />
                </div>
                <div className="mt-1 flex items-center gap-1" data-id="element-2648">
                  <Instagram className="w-2.5 h-2.5" style={{
                  color: '#0d9488'
                }} data-id="element-2649" />
                  <p className="text-[7px] uppercase tracking-wider leading-tight" style={{
                  color: '#6b7280'
                }} data-id="element-2650">
                    Follow us
                  </p>
                </div>
              </div>

              {/* QR 2: WhatsApp */}
              <div className="flex flex-col items-center" data-id="element-2651">
                <div className="w-16 h-16 rounded-md overflow-hidden p-1" style={{
                border: '1px solid #99f6e4',
                backgroundColor: '#ffffff'
              }} data-id="element-2652">
                  <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://wa.me/447345197820&format=png&color=0d4f4f&bgcolor=ffffff" alt="WhatsApp QR Code" className="w-full h-full object-contain" data-id="element-2653" />
                </div>
                <div className="mt-1 flex items-center gap-1" data-id="element-2654">
                  <Phone className="w-2.5 h-2.5" style={{
                  color: '#0d9488'
                }} data-id="element-2655" />
                  <p className="text-[7px] uppercase tracking-wider leading-tight" style={{
                  color: '#6b7280'
                }} data-id="element-2656">
                    via WhatsApp
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════ */}
      {/* BOTH SIDES ON ONE PAGE (A4 Landscape Preview) */}
      {/* ═══════════════════════════════════════════ */}
      <div className="no-print mb-4 mt-6 text-sm font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2657">
        Both Sides — One Page
      </div>
      <div className="single-panel overflow-hidden relative print-panel-single flex" style={{
      backgroundColor: '#ffffff',
      WebkitPrintColorAdjust: 'exact',
      printColorAdjust: 'exact',
      boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
    } as React.CSSProperties} data-id="element-2658">
        {/* Fold line */}
        <div className="absolute left-1/2 top-0 bottom-0 w-px border-l border-dashed z-50" style={{
        borderColor: 'rgba(200,200,200,0.4)'
      }} data-id="element-2659"></div>

        {/* LEFT — Front Side */}
        <div className="w-1/2 h-full relative flex flex-col items-center justify-center text-center" style={{
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact'
      } as React.CSSProperties} data-id="element-2660">
          <img src="https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&w=1200&q=80" alt="Dentist and patient smiling" className="absolute inset-0 w-full h-full object-cover z-0" data-id="element-2661" />
          <div className="absolute inset-0 z-[1]" style={{
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.50), rgba(0,0,0,0.35), rgba(0,0,0,0.55))',
          WebkitPrintColorAdjust: 'exact',
          printColorAdjust: 'exact'
        } as React.CSSProperties} data-id="element-2662"></div>
          <div className="absolute inset-4 border z-[2] pointer-events-none" style={{
          borderColor: 'rgba(255,255,255,0.25)'
        }} data-id="element-2663"></div>
          <div className="absolute inset-6 border z-[2] pointer-events-none" style={{
          borderColor: 'rgba(255,255,255,0.10)'
        }} data-id="element-2664"></div>

          <div className="relative z-[3] flex flex-col items-center w-full h-full justify-between py-8 px-8" data-id="element-2665">
            <div className="flex flex-col items-center" data-id="element-2666">
              <div className="rounded-xl p-2.5 mb-3" style={{
              backgroundColor: 'rgba(255,255,255,0.95)'
            }} data-id="element-2667">
                <img src="https://cdn.magicpatterns.com/uploads/oHJPxkkc7f6GgU5uyo3LQX/ChatGPT_Image_Feb_5,_2026,_03_12_40_PM.png" alt="Dentico Clinic Logo" className="h-10 w-auto object-contain" data-id="element-2668" />
              </div>
              <span className="text-xl font-bold tracking-tight leading-none mb-1.5" style={{
              color: '#ffffff',
              textShadow: '0 1px 3px rgba(0,0,0,0.3)'
            }} data-id="element-2669">
                DenticoClinic
              </span>
              <p className="text-xs uppercase leading-tight" style={{
              color: 'rgba(255,255,255,0.85)',
              letterSpacing: '0.3em',
              textShadow: '0 1px 2px rgba(0,0,0,0.3)'
            }} data-id="element-2670">
                Private Dentistry
              </p>
            </div>
            <div className="flex flex-col items-center w-full" data-id="element-2671">
              <div className="w-10 h-px mb-5" style={{
              backgroundColor: 'rgba(255,255,255,0.4)'
            }} data-id="element-2672"></div>
              <p className="text-[10px] uppercase font-medium mb-4" style={{
              color: 'rgba(255,255,255,0.75)',
              letterSpacing: '0.4em',
              textShadow: '0 1px 2px rgba(0,0,0,0.3)'
            }} data-id="element-2673">
                Gift Voucher
              </p>
              <h2 className="text-3xl font-serif mb-6 leading-tight" style={{
              color: '#ffffff',
              textShadow: '0 1px 4px rgba(0,0,0,0.3)'
            }} data-id="element-2674">
                Professional
                <br data-id="element-2675" />
                Teeth Whitening
              </h2>
              <div className="inline-block py-3 px-10" style={{
              borderTop: '1px solid rgba(255,255,255,0.35)',
              borderBottom: '1px solid rgba(255,255,255,0.35)'
            }} data-id="element-2676">
                <p className="text-[9px] uppercase mb-1.5" style={{
                color: 'rgba(255,255,255,0.65)',
                letterSpacing: '0.3em'
              }} data-id="element-2677">
                  Value
                </p>
                <p className="text-2xl font-serif font-bold tracking-wide" style={{
                color: '#ffffff',
                textShadow: '0 1px 3px rgba(0,0,0,0.3)'
              }} data-id="element-2678">
                  £280
                </p>
              </div>
            </div>
            <div className="mt-auto" data-id="element-2679">
              <p className="text-[10px]" style={{
              color: 'rgba(255,255,255,0.65)',
              letterSpacing: '0.2em',
              textShadow: '0 1px 2px rgba(0,0,0,0.3)'
            }} data-id="element-2680">
                denticoclinic.co.uk
              </p>
            </div>
          </div>
        </div>

        {/* RIGHT — Back Side */}
        <div className="w-1/2 h-full relative flex flex-col py-6 px-8 items-center text-center" style={{
        backgroundColor: '#fafaf9',
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact'
      } as React.CSSProperties} data-id="element-2681">
          <div className="absolute inset-4 pointer-events-none" style={{
          border: '1px solid rgba(15,60,60,0.10)'
        }} data-id="element-2682"></div>
          <div className="absolute inset-6 pointer-events-none" style={{
          border: '1px solid rgba(15,60,60,0.05)'
        }} data-id="element-2683"></div>

          <div className="relative z-10 flex flex-col items-center h-full w-full" data-id="element-2684">
            {/* Logo */}
            <div className="flex flex-col items-center mb-4 mt-1" data-id="element-2685">
              <img src="https://cdn.magicpatterns.com/uploads/oHJPxkkc7f6GgU5uyo3LQX/ChatGPT_Image_Feb_5,_2026,_03_12_40_PM.png" alt="Dentico Clinic Logo" className="h-7 w-auto object-contain mb-1" data-id="element-2686" />
              <span className="text-sm font-bold tracking-tight leading-none mb-0.5" style={{
              color: '#111827'
            }} data-id="element-2687">
                Dentico
                <span style={{
                color: '#0d9488'
              }} data-id="element-2688">
                  Clinic
                </span>
              </span>
              <p className="text-[8px] uppercase leading-tight" style={{
              color: '#6b7280',
              letterSpacing: '0.2em'
            }} data-id="element-2689">
                Private Dentistry
              </p>
            </div>

            <h1 className="text-lg font-serif mb-3 tracking-wide" style={{
            color: '#134e4a'
          }} data-id="element-2690">
              Gift Voucher
            </h1>

            <div className="max-w-[90%] space-y-2 mb-4" data-id="element-2691">
              <p className="text-[11px] leading-relaxed" style={{
              color: '#4b5563'
            }} data-id="element-2692">
                Congratulations! You have received a complimentary professional
                teeth whitening experience at Dentico Dental Clinic.
              </p>
              <p className="text-[11px] leading-relaxed" style={{
              color: '#4b5563'
            }} data-id="element-2693">
                We look forward to welcoming you to our clinic and helping you
                achieve a brighter, confident smile.
              </p>
            </div>

            <div className="mb-4 w-full max-w-[85%]" data-id="element-2694">
              <h3 className="text-[9px] font-bold uppercase tracking-widest mb-2" style={{
              color: '#115e59'
            }} data-id="element-2695">
                What's Included
              </h3>
              <ul className="space-y-1.5 text-left inline-block" data-id="element-2696">
                {['Professional clinical teeth whitening', 'Consultation with our dentist', 'Personalised treatment approach'].map((item, idx) => <li key={idx} className="flex items-start gap-2" data-id="element-2697">
                    <div className="mt-0.5 w-3 h-3 rounded-full flex items-center justify-center flex-shrink-0" style={{
                  backgroundColor: '#ccfbf1',
                  WebkitPrintColorAdjust: 'exact',
                  printColorAdjust: 'exact'
                } as React.CSSProperties} data-id="element-2698">
                      <Check className="w-1.5 h-1.5" style={{
                    color: '#0f766e'
                  }} data-id="element-2699" />
                    </div>
                    <span className="text-[11px]" style={{
                  color: '#374151'
                }} data-id="element-2700">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>

            <div className="flex items-end justify-center gap-6 mb-3" data-id="element-2701">
              <div data-id="element-2702">
                <label className="block text-[8px] uppercase tracking-widest mb-0.5" style={{
                color: '#6b7280'
              }} data-id="element-2703">
                  Unique Code
                </label>
                <div contentEditable suppressContentEditableWarning className="text-xs font-mono text-center pb-0.5 min-h-[1.3em] focus:outline-none" style={{
                color: '#374151',
                borderBottom: '1px solid #d1d5db',
                minWidth: '70px'
              }} data-id="element-2704">
                  DC-RAFFLE-01
                </div>
              </div>
              <p className="text-[9px] tracking-wider pb-0.5" style={{
              color: '#6b7280'
            }} data-id="element-2705">
                Valid until: 30 June 2026
              </p>
            </div>

            <p className="text-[8px] uppercase tracking-wider mb-3" style={{
            color: '#9ca3af'
          }} data-id="element-2706">
              By appointment only • Non-transferable
            </p>

            {/* Footer */}
            <div className="mt-auto w-full flex flex-col items-center" data-id="element-2707">
              <div className="flex flex-col items-center text-[9px] mb-3 leading-relaxed" style={{
              color: '#4b5563'
            }} data-id="element-2708">
                <div className="flex items-center gap-1 mb-0.5" data-id="element-2709">
                  <MapPin className="w-2 h-2" style={{
                  color: '#0d9488'
                }} data-id="element-2710" />
                  <span className="font-medium" style={{
                  color: '#374151'
                }} data-id="element-2711">
                    Central London – Angel
                  </span>
                </div>
                <span data-id="element-2712">360–364 City Road, London EC1V 2PY</span>
                <div className="flex items-center justify-center gap-3 mt-1" data-id="element-2713">
                  <div className="flex items-center gap-1" data-id="element-2714">
                    <Phone className="w-2 h-2" style={{
                    color: '#0d9488'
                  }} data-id="element-2715" />
                    <span data-id="element-2716">+44 7345 197820</span>
                  </div>
                  <div className="flex items-center gap-1" data-id="element-2717">
                    <Instagram className="w-2 h-2" style={{
                    color: '#0d9488'
                  }} data-id="element-2718" />
                    <span data-id="element-2719">@dentico_clinic</span>
                  </div>
                </div>
              </div>

              <div className="flex items-start justify-center gap-6" data-id="element-2720">
                <div className="flex flex-col items-center" data-id="element-2721">
                  <div className="w-12 h-12 rounded overflow-hidden p-0.5" style={{
                  border: '1px solid #99f6e4',
                  backgroundColor: '#ffffff'
                }} data-id="element-2722">
                    <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://instagram.com/dentico_clinic&format=png&color=0d4f4f&bgcolor=ffffff" alt="Instagram QR" className="w-full h-full object-contain" data-id="element-2723" />
                  </div>
                  <div className="mt-0.5 flex items-center gap-0.5" data-id="element-2724">
                    <Instagram className="w-2 h-2" style={{
                    color: '#0d9488'
                  }} data-id="element-2725" />
                    <p className="text-[6px] uppercase tracking-wider" style={{
                    color: '#6b7280'
                  }} data-id="element-2726">
                      Follow us
                    </p>
                  </div>
                </div>
                <div className="flex flex-col items-center" data-id="element-2727">
                  <div className="w-12 h-12 rounded overflow-hidden p-0.5" style={{
                  border: '1px solid #99f6e4',
                  backgroundColor: '#ffffff'
                }} data-id="element-2728">
                    <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://wa.me/447345197820&format=png&color=0d4f4f&bgcolor=ffffff" alt="WhatsApp QR" className="w-full h-full object-contain" data-id="element-2729" />
                  </div>
                  <div className="mt-0.5 flex items-center gap-0.5" data-id="element-2730">
                    <Phone className="w-2 h-2" style={{
                    color: '#0d9488'
                  }} data-id="element-2731" />
                    <p className="text-[6px] uppercase tracking-wider" style={{
                    color: '#6b7280'
                  }} data-id="element-2732">
                      WhatsApp
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════ */}
      {/* PRINT STYLES                               */}
      {/* ═══════════════════════════════════════════ */}
      <style data-id="element-2733">{`
        /* ── Screen preview ── */
        @media screen {
          .print-panel {
            width: 100%;
            max-width: 148.5mm;
            aspect-ratio: 148.5 / 210;
          }
          .print-panel-single {
            width: 100%;
            max-width: 297mm;
            aspect-ratio: 297 / 210;
          }
        }

        /* ── Print: shared rules ── */
        @media print {
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
          }

          .no-print {
            display: none !important;
          }

          img {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          svg {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .print-panel {
            box-shadow: none !important;
            margin: 0 !important;
            border-radius: 0 !important;
          }
        }

        /* ── Print FRONT only ── */
        @media print {
          .print-mode-front .back-panel {
            display: none !important;
          }
          .print-mode-front .front-panel {
            width: 148mm !important;
            height: 210mm !important;
            max-width: none !important;
            aspect-ratio: auto !important;
            page-break-after: avoid !important;
          }
          .print-mode-front {
            padding: 0 !important;
            margin: 0 !important;
          }
        }

        /* ── Print BACK only ── */
        @media print {
          .print-mode-back .front-panel {
            display: none !important;
          }
          .print-mode-back .back-panel {
            width: 148mm !important;
            height: 210mm !important;
            max-width: none !important;
            aspect-ratio: auto !important;
            page-break-after: avoid !important;
          }
          .print-mode-back {
            padding: 0 !important;
            margin: 0 !important;
          }
        }

        /* ── Print BOTH (A4 Landscape, side by side) ── */
        @media print {
          .print-mode-both {
            padding: 0 !important;
            margin: 0 !important;
          }
          .print-mode-both .front-panel,
          .print-mode-both .back-panel {
            width: 148.5mm !important;
            height: 210mm !important;
            max-width: none !important;
            aspect-ratio: auto !important;
            display: inline-block !important;
            vertical-align: top !important;
            float: left !important;
          }
          .print-mode-both .single-panel {
            display: none !important;
          }
        }

        /* ── Print SINGLE (A4 Landscape, both sides on one page) ── */
        @media print {
          .print-mode-single .front-panel,
          .print-mode-single .back-panel {
            display: none !important;
          }
          .print-mode-single .single-panel {
            width: 297mm !important;
            height: 210mm !important;
            max-width: none !important;
            aspect-ratio: auto !important;
            page-break-after: avoid !important;
            box-shadow: none !important;
            border-radius: 0 !important;
            margin: 0 !important;
            display: flex !important;
          }
          .print-mode-single {
            padding: 0 !important;
            margin: 0 !important;
          }
        }

        /* ── Hide single panel in front/back modes ── */
        @media print {
          .print-mode-front .single-panel,
          .print-mode-back .single-panel {
            display: none !important;
          }
        }

        /* ── @page rules per mode ── */
        .print-mode-front ~ style,
        .print-mode-back ~ style {
          /* Handled by dynamic @page below */
        }
      `}</style>

      {/* Dynamic @page size based on print mode */}
      {printMode === 'front' || printMode === 'back' ? <style data-id="element-2734">{`
          @media print {
            @page {
              size: 148mm 210mm;
              margin: 0;
            }
          }
        `}</style> : printMode === 'both' ? <style data-id="element-2735">{`
          @media print {
            @page {
              size: A4 landscape;
              margin: 0;
            }
          }
        `}</style> : printMode === 'single' ? <style data-id="element-2736">{`
          @media print {
            @page {
              size: A4 landscape;
              margin: 0;
            }
          }
        `}</style> : null}
    </div>;
}